import { useEffect, useMemo, useState } from "react";
import { CalendarCheck, Info, ShieldCheck } from "lucide-react";
import { api } from "@/lib/api";
import { Link, useLang, useT } from "@/i18n";
import StayPicker from "@/components/public/StayPicker";

const LOCALE_INTL = { fr: "fr-FR", en: "en-GB", de: "de-DE", it: "it-IT" };
export function useMoney() {
  const lang = useLang();
  return (n) => new Intl.NumberFormat(LOCALE_INTL[lang] || "fr-FR", { style: "currency", currency: "EUR", maximumFractionDigits: n % 1 ? 2 : 0 }).format(n || 0);
}
export function useDay() {
  const lang = useLang();
  return (s) => (s ? new Date(`${s}T12:00:00`).toLocaleDateString(LOCALE_INTL[lang] || "fr-FR", { weekday: "short", day: "numeric", month: "long", year: "numeric" }) : "");
}

export function QuoteDetails({ quote, testId = "quote" }) {
  const t = useT();
  const money = useMoney();
  const day = useDay();
  if (!quote?.ok) return null;
  return (
    <div className="rounded-xl bg-ecru/70 p-4 text-sm" data-testid={testId}>
      <ul className="space-y-1.5">
        {quote.lines.map((l, i) => (
          <li key={i} className="flex justify-between gap-3" data-testid={`${testId}-line-${l.kind}`}>
            <span className="text-charcoal/80">{l.kind === "nights" ? t("{n} nuit(s) × {p}", { n: l.qty, p: money(l.unit_price) }) : `${l.label}${l.qty > 1 ? ` × ${l.qty}` : ""}`}</span>
            <span className="text-forest">{money(l.amount)}</span>
          </li>
        ))}
      </ul>
      {quote.on_request?.length > 0 && <p className="text-xs text-charcoal/60 mt-2">{t("Sur demande (non inclus) :")} {quote.on_request.join(", ")}</p>}
      <div className="flex justify-between border-t border-sand mt-3 pt-3 font-medium text-forest" data-testid={`${testId}-total`}><span>{t("Total")}</span><span>{money(quote.total)}</span></div>
      {quote.full_payment_now ? (
        <p className="mt-2 text-forest" data-testid={`${testId}-deposit`}>{t("Arrivée dans moins de {n} jours : règlement de la totalité à la réservation.", { n: 7 })}</p>
      ) : (
        <div className="mt-2 space-y-1 text-charcoal/80">
          <p className="flex justify-between" data-testid={`${testId}-deposit`}><span>{t("Acompte ({p} %), demandé après confirmation de l'hôte", { p: quote.deposit_percent })}</span><span className="text-forest font-medium">{money(quote.deposit)}</span></p>
          <p className="flex justify-between" data-testid={`${testId}-balance`}><span>{t("Solde avant le {d}", { d: day(quote.balance_due_date) })}</span><span>{money(quote.balance)}</span></p>
        </div>
      )}
      {quote.security_deposit > 0 && <p className="text-xs text-charcoal/60 mt-3 inline-flex gap-1.5"><ShieldCheck size={14} className="text-sage shrink-0" /> {t("Caution de {a} par empreinte bancaire (non débitée sauf dommage).", { a: money(quote.security_deposit) })}</p>}
    </div>
  );
}

/** Bloc « Disponibilités & tarif » de la fiche appartement. */
export default function BookingWidget({ apt, options = [] }) {
  const t = useT();
  const [av, setAv] = useState(null);
  const [stay, setStay] = useState({ start: "", end: "" });
  const [guests, setGuests] = useState(Math.min(2, apt.capacity || 2));
  const [opts, setOpts] = useState({ cleaning: false, linen: false, towels: false, pet: false });
  const [quote, setQuote] = useState(null);
  useEffect(() => { api.get(`/public/booking/apartments/${apt.slug}/availability`).then((r) => setAv(r.data)).catch(() => setAv(null)); }, [apt.slug]);
  useEffect(() => {
    if (!av?.priced || !stay.start || !stay.end) { setQuote(null); return undefined; }
    const h = setTimeout(() => api.post("/public/booking/quote", { apartment: apt.slug, ...stay, guests, ...opts }).then((r) => setQuote(r.data)).catch(() => setQuote(null)), 250);
    return () => clearTimeout(h);
  }, [av, stay, guests, opts, apt.slug]);
  const optionList = useMemo(() => options.filter((o) => ["cleaning", "linen", "towels", "pet"].includes(o.key) && o.active !== false), [options]);
  if (!av) return null;
  const qs = new URLSearchParams({ appartement: apt.slug, ...(stay.start && { arrivee: stay.start }), ...(stay.end && { depart: stay.end }), voyageurs: String(guests) });
  Object.entries(opts).forEach(([k, v]) => v && qs.set(k, "1"));
  // Paiement en ligne de l'acompte (Stripe + réservation en ligne ouverte) ; sinon « Réserver » = demande de réservation directe
  const canPay = av.online_booking && av.online_payment && quote?.ok;
  return (
    <section className="card p-5 sm:p-7" data-testid="booking-widget">
      <p className="eyebrow mb-2">{t("Disponibilités")}</p>
      <h2 className="font-serif text-3xl text-forest mb-5">{av.priced ? t("Choisissez vos dates") : t("Calendrier des disponibilités")}</h2>
      <StayPicker busy={av.busy} rules={av.rules} value={stay} onChange={setStay} testId="apartment-stay-picker" prices={av.priced ? av.prices || {} : null} />
      <div className="grid sm:grid-cols-2 gap-4 mt-5">
        <div>
          <label className="label" htmlFor="bw-guests">{t("Voyageurs")}</label>
          <select id="bw-guests" className="input" value={guests} onChange={(e) => setGuests(Number(e.target.value))} data-testid="booking-widget-guests">
            {Array.from({ length: apt.capacity || 4 }, (_, i) => i + 1).map((n) => <option key={n} value={n}>{t("{n} personne(s)", { n })}</option>)}
          </select>
        </div>
        {av.priced && optionList.length > 0 && (
          <fieldset>
            <legend className="label">{t("Options")}</legend>
            <div className="flex flex-wrap gap-2">
              {optionList.map((o) => (
                <label key={o.key} className={`cursor-pointer px-3 py-2 rounded-full border text-xs transition ${opts[o.key] ? "bg-forest text-ecru border-forest" : "bg-white border-sand text-forest"}`} data-testid={`booking-widget-opt-${o.key}`}>
                  <input type="checkbox" className="sr-only" checked={opts[o.key]} onChange={(e) => setOpts({ ...opts, [o.key]: e.target.checked })} /> {o.name}
                </label>
              ))}
            </div>
          </fieldset>
        )}
      </div>
      {quote && !quote.ok && quote.errors?.length > 0 && <p className="text-sm text-red-700 mt-4" data-testid="booking-widget-error">{quote.errors[0]}</p>}
      {quote && !quote.ok && !quote.errors?.length && !quote.priced && <p className="text-sm text-charcoal/70 mt-4 inline-flex gap-1.5"><Info size={15} className="shrink-0 mt-0.5" /> {t("Tarif sur demande pour ces dates.")}</p>}
      <div className="mt-5"><QuoteDetails quote={quote} testId="booking-widget-quote" /></div>
      <div className="mt-5 flex flex-col sm:flex-row gap-3">
        {canPay && <Link to={`/reservation?${qs.toString()}`} className="btn-primary flex-1 py-4" data-testid="booking-widget-pay"><CalendarCheck size={18} /> {t("Réserver et payer l'acompte")}</Link>}
        <Link to={`/demande-de-disponibilite?${qs.toString()}`} className={`${canPay ? "btn-secondary" : "btn-primary"} flex-1 py-4`} data-testid={av.priced ? "booking-widget-book" : "booking-widget-request"}>
          <CalendarCheck size={18} /> {av.priced ? t("Réserver") : t("Demander une disponibilité")}</Link>
      </div>
    </section>
  );
}
